import styles from "./MusicSuggestionsDialog.module.scss";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import { MusicSearchBar } from "./components/MusicSearchBar";
import { MusicSuggestionsList } from "./components/MusicSuggestionsList";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { useMusicSuggestions } from "./hooks/useMusicSuggestions";
import { useAudioPlayer } from "./hooks/useAudioPlayer";
import { musicEndpoints } from "./utilities/musicEndpoints";
import {
  getSuccessToast,
  getErrorToast,
} from "../../../common/infrastructure/utilities/toastUtility";
import { useQueryClient } from "@tanstack/react-query";
import { copyToClipboard } from "../../../common/utilities/clipboardUtility";
import { Button } from "primereact/button";
import { useAppContext } from "../../../AppContext";

interface IMusicSuggestionsDialogProps {
  eventId: number;
  isOpen: boolean;
  onClose: () => void;
  showSearch?: boolean;
  isAdminView?: boolean; // True when opened by instructor/admin from planner
}

export const MusicSuggestionsDialog = ({
  eventId,
  isOpen,
  onClose,
  showSearch = true,
  isAdminView = false,
}: IMusicSuggestionsDialogProps) => {
  const queryClient = useQueryClient();
  const addSuggestionMutation = useApiMutation();
  const audioPlayer = useAudioPlayer();
  const { selectedStudio } = useAppContext();

  const {
    suggestions,
    isLoading,
    eventEndDateTime,
    allowMusicSuggestions,
    handleDelete,
    handleUpvote,
  } = useMusicSuggestions(eventId);

  // Check if event has ended
  const eventHasEnded = eventEndDateTime
    ? new Date(eventEndDateTime) < new Date()
    : false;

  // Only show search if user has reservation AND event hasn't ended AND feature is enabled
  const canSuggestMusic = showSearch && !eventHasEnded && allowMusicSuggestions;

  const handleCopyShareLink = async () => {
    // Generate share link for appointment wizard (public route)
    // Use studio URL if available, otherwise fallback to browser origin
    const baseUrl = selectedStudio?.url || window.location.origin;
    const shareUrl = `${baseUrl}/appointment?musicSuggest=true&eventId=${eventId}`;

    const success = await copyToClipboard(shareUrl);

    if (success) {
      getSuccessToast("Линкът е копиран в клипборда!");
    } else {
      getErrorToast("Грешка при копиране на линка");
    }
  };

  const handleGoToReservation = () => {
    // Use studio URL if available, otherwise fallback to browser origin
    const baseUrl = selectedStudio?.url || window.location.origin;
    const reservationUrl = `${baseUrl}/calendar?eventId=${eventId}`;
    window.location.href = reservationUrl;
  };

  const handleTrackSelect = async (trackId: string) => {
    try {
      const response: any = await addSuggestionMutation.mutateAsync({
        endpoint: musicEndpoints.addSuggestion(eventId),
        method: "post",
        payload: { trackId },
        disableSuccessToast: true,
      });

      // Invalidate to refresh the list
      queryClient.invalidateQueries({ queryKey: ["musicSuggestions", eventId] });

      // Show appropriate message based on action
      const message = response?.message || "Песента е добавена успешно!";
      getSuccessToast(message);
    } catch (error: any) {
      getErrorToast(
        error?.response?.data?.message || "Грешка при добавяне на песента",
      );
    }
  };

  return (
    <AppDialog
      header="🎵 Музикални предложения"
      visible={isOpen}
      onHide={onClose}
      className={styles.musicDialog}
      contentClassName={styles.dialogContent}
      style={{ width: "90vw", maxWidth: "800px", height: "80vh" }}
    >
      {/* Info section */}
      <div className={styles.infoSection}>
        <div className={styles.infoCard}>
          <i className="pi pi-info-circle" />
          <div className={styles.infoText}>
            {canSuggestMusic ? (
              <>
                <strong>Предложете музика за класа</strong>
                <p>
                  Можете да предложите любимите си песни и да гласувате за
                  песни, които други хора са предложили.
                </p>
              </>
            ) : isAdminView ? (
              <>
                <strong>Преглед на музикални предложения</strong>
                <p>
                  Тук можете да видите песните, предложени от клиентите за този
                  клас.
                </p>
                <Button
                  label="Копирай линк за споделяне"
                  icon="pi pi-share-alt"
                  onClick={handleCopyShareLink}
                  className={styles.shareButton}
                  size="small"
                  outlined
                />
              </>
            ) : eventHasEnded ? (
              <>
                <strong>Събитието приключи</strong>
                <p>
                  Музикални предложения не могат да бъдат добавени за минали събития.
                </p>
              </>
            ) : !allowMusicSuggestions ? (
              <>
                <strong>Музикалните предложения са изключени</strong>
                <p>
                  Музикалните предложения са деактивирани за този клас.
                </p>
              </>
            ) : (
              <>
                <strong>Преглед на музикални предложения</strong>
                <p>
                  За да предложите музика, първо направете резервация за този клас.
                </p>
                <Button
                  label="Направи резервация"
                  icon="pi pi-calendar-plus"
                  onClick={handleGoToReservation}
                  className={styles.reservationButton}
                  size="small"
                  outlined
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search section - only if user can suggest music */}
      {canSuggestMusic && (
        <div className={styles.searchSection}>
          <MusicSearchBar
            onTrackSelect={handleTrackSelect}
            audioPlayer={audioPlayer}
          />
        </div>
      )}

      {/* Suggestions list section */}
      <div className={styles.suggestionsSection}>
        <div className={styles.sectionHeader}>
          <h3>
            <i className="pi pi-list" /> Предложения
          </h3>
        </div>
        <MusicSuggestionsList
          suggestions={suggestions}
          isLoading={isLoading}
          isAdminView={isAdminView}
          hasActiveReservation={canSuggestMusic}
          onDelete={handleDelete}
          onUpvote={handleUpvote}
          audioPlayer={audioPlayer}
        />
      </div>
    </AppDialog>
  );
};
